<template lang="pug">
.right-panel
  .px-4.pt-4
    .d-flex.mb-2
      .text-md.font-semibold.mb-4.flex-grow-1 {{ $t('patient_history') }}
      v-btn.ml-1.mr-2(@click="toggleRightPanel" icon small color="primary")
        v-icon(size=22) $yzi-layout-left-filled
    v-btn-toggle.round-xl.mb-4.pr-3(v-model="category" mandatory style="width: 100%" dense)
      v-btn.flex-grow-1.white
        .text-none.font-semibold.text-sm(:class="getToggleBtnClass(0)") {{ $t('all') }}
      v-btn.flex-grow-1.white
        .text-none.font-semibold.text-sm(:class="getToggleBtnClass(1)") Dx
      v-btn.flex-grow-1.white
        .text-none.font-semibold.text-sm(:class="getToggleBtnClass(2)") Meds
      v-btn.flex-grow-1.white
        .text-none.font-semibold.text-sm(:class="getToggleBtnClass(3)") Servs
      v-btn.flex-grow-1.white
        .text-none.font-semibold.text-sm(:class="getToggleBtnClass(4)") Docs
    div
      .d-flex.align-center.mb-4
        v-menu(offset-y bottom)
          template(v-slot:activator="{ on }")
            v-btn.text-none(small rounded outlined v-on="on")
              span.text-sm.mr-1 {{ selectedRange.text }}
              v-icon(size="16") $yzi-chevron-down
          v-list.py-0(min-width="200px")
            template(v-for="(range, historyRangeIndex) in historyRange")
              v-divider(v-if="historyRangeIndex !== 0")
              v-list-item(@click="onSelectHistoryRange(range)")
                v-list-item-content
                  v-list-item-title.text-sm {{ range.text }}
        v-spacer
        v-btn(icon color="neutral500" @click="onClickSearch")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="onBlurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
        v-menu(offset-y bottom)
          template(v-slot:activator="{ on }")
            v-btn(v-if="selectedSort.text === $t('clear_sort')" icon color="neutral500" v-on="on")
              v-icon(size="20") $yzi-filter-line
            v-btn.selected-button(v-else text small color="neutral" v-on="on")
              v-icon(size="20") $yzi-menu-03
              v-icon(size="13") {{ selectedSort.text === $t('newest_to_oldest') ? '$yzi-arrow-up' : '$yzi-arrow-down' }}
          v-list.py-0(min-width="200px")
            .text-md.font-semibold.px-4.py-2 {{ $t('sort') }}
            template(v-for="(sort, sortItemIndex) in sortItems")
              v-divider(v-if="sortItemIndex !== 0")
              v-list-item(@click="onSelectSort(sort)")
                v-list-item-content
                  v-list-item-title.text-sm(:class="sort.text === $t('clear_sort') ? 'danger500--text' : ''") {{ sort.text }}
  template(v-if="isFetching")
    v-skeleton-loader.px-4(type="list-item-three-line@3")
  template(v-else-if="totalCount === 0")
    .d-flex.align-center.justify-center.fill-height
      .text-sm.neutral400--text {{ $t('item_not_found', { item: $t('patient_history').toLowerCase() }) }}
  template(v-else)
    .px-4.pb-4.scrollable(:style="`height: ${height}`")
      template(v-if="category === 2")
        patient-history-item-active-medication(:parentActiveMedications="parentActiveMedications.data" @updated="$emit('updateActiveMedications')")
      template(v-for="history in patientHistories")
        patient-history-item(:parentRegistration="parentRegistration" :parentHistory="history" :category="category" @updated="$emit('updated')" @viewDetails="onViewDetails(history)")
    v-divider
    div.d-flex.align-center.py-2.pr-6.pl-4
      y-button(v-if="!hideViewAll" label="View all visit summary" size="sm" @click="openAllVisitSummaryDialog = true")
      v-spacer
      div
        yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")
  .dialogs
    patient-history-view-details-dialog(v-if="openPatientHistoryViewDetailsDialog" :open.sync="openPatientHistoryViewDetailsDialog" :parentRegistration="parentRegistration" :selectedHistory="selectedHistory" :category="category" @updated="$emit('updated')")
    all-visit-summary-dialog(v-if="openAllVisitSummaryDialog" :open.sync="openAllVisitSummaryDialog" :patient="parentPatient" @updated="$emit('updated')")
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import PatientHistoryItem from '@/components/clinic/consultation/patientHistories/PatientHistoryItem.vue'
import PatientHistoryItemActiveMedication from '@/components/clinic/consultation/patientHistories/PatientHistoryItemActiveMedication.vue'
import PatientHistoryViewDetailsDialog from '@/components/clinic/dialogs/PatientHistoryViewDetailsDialog.vue'
import AllVisitSummaryDialog from '@/components/clinic/dialogs/AllVisitSummaryDialog.vue'

export default {
  components: {
    PatientHistoryItem, PatientHistoryItemActiveMedication, PatientHistoryViewDetailsDialog, AllVisitSummaryDialog
  },
  props: {
    parentPatient: {
      type: Object,
      required: true
    },
    parentRegistration: {
      type: Object,
      required: true
    },
    showRightPanel: {
      type: Boolean,
      required: true
    },
    height: {
      type: String,
      default: ''
    },
    hideViewAll: {
      type: Boolean,
      default: false
    },
    parentActiveMedications: {
      type: Object,
      default: () => {
        return {
          isFetching: false,
          isError: false,
          data: [],
        }
      }
    }
  },
  data() {
    return {
      isFetching: false,
      showSearchField: false,
      openPatientHistoryViewDetailsDialog: false,
      openAllVisitSummaryDialog: false,
      patientHistories: null,
      searchText: null,
      selectedHistory: null,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      category: 0,
      selectedRange: { text: this.$t('all_time'), value: null },
      selectedSort: { text:this.$t('clear_sort'), value:null },
      historyRangeOptions: [
        { text: this.$t('all_time'), value: null },
        { text: this.$t('past_1_month'), value: 1 },
        { text: this.$t('past_3_months'), value: 3 },
        { text: this.$t('past_6_months'), value: 6 },
        { text: this.$t('past_12_months'), value: 12 },
      ],
      sortOptions: [
        { text:this.$t('newest_to_oldest'), value:'-created' },
        { text:this.$t('oldest_to_newest'), value:'created' },
        { text:this.$t('clear_sort'), value:null },
      ]
    }
  },
  watch: {
    currentPage() {
      this.fetchPatientHistory()
    },
    pageSize() {
      this.fetchPatientHistory()
    },
    selectedRange() {
      this.fetchPatientHistory()
    },
    selectedSort() {
      this.fetchPatientHistory()
    },
  },
  mounted () {
    this.fetchPatientHistory()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    historyRange() {
      return this.historyRangeOptions.filter(item => this.selectedRange.text !== item.text)
    },
    sortItems() {
      return this.sortOptions.filter(item => this.selectedSort.text !== item.text)
    },
    params() {
      return {
        search: this.searchText,
        past_months: this.selectedRange.value,
        page_size: this.pageSize,
        page: this.currentPage,
        ordering: this.selectedSort.value
      }
    },
    togglePanel: {
      get () {
        return this.showRightPanel
      },
      set (newValue) {
        this.$emit('update:showRightPanel', newValue)
      }
    },
    // height () {
    //   const screenHeight = screen.height
    //   const height = 30/100*screenHeight
    //   return`height: ${height}px`
    // }
  },
  methods: {
    ...mapActions(['showMessage']),
    pageChanged(page) {
      this.currentPage = page
    },
    onSelectHistoryRange(selectedRange) {
      this.selectedRange = selectedRange
    },
    onSelectSort(selectedSort) {
      this.selectedSort = selectedSort
    },
    getToggleBtnClass(index) {
      return this.category === index ? '' : 'neutral500--text'
    },
    queryChanged: debounce(function () {
      this.fetchPatientHistory()
    }, 750),
    onClickSearch() {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true
      }
    },
    onBlurSearch() {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    onViewDetails(selected) {
      this.selectedHistory = selected
      this.openPatientHistoryViewDetailsDialog = true
    },
    async fetchPatientHistory() {
      try {
        this.isFetching = true
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/patients/${this.parentPatient.id}/histories`, { params: this.params })
        let histories = data.results
        histories.forEach(history => {
          if (history.treatments.length) {
            history.medications = history.treatments.filter(f => ['medication', 'equipment', 'consumable'].includes(f.product.type))
            history.services = history.treatments.filter(f => ['service', 'procedure', 'investigation', 'consultation'].includes(f.product.type))
            history.documents = history.treatments.filter(f => f.product.type === 'documents')
          }
        })
        this.patientHistories = histories
        this.totalCount = data.count
        this.isFetching = false
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.patientHistories = []
          this.totalCount = 0
          this.isFetching = false
          return
        }
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
      this.checkParams()
    },
    checkParams () {
      if (this.$route.params?.viewAllHistory) {
        this.openAllVisitSummaryDialog = true
      }
    },
    toggleRightPanel () {
      this.togglePanel = !this.togglePanel
    },
  },
};
</script>

<style lang="scss" scoped>
.scrollable {
  overflow-y: auto;
}
.selected-button {
  background: #EEEEEE !important;
  border-radius: 16px;
}
</style>